import React, { useState } from 'react';
import { t } from '../tokens';

interface HeaderProps {
  onHome: () => void;
  onOpenChart: () => Promise<void>;
  onSetlists: () => Promise<void>;
  onNewChart: () => void;
  onOpenNewChartChoice?: () => void;
  onPianoRec?: () => void;
  onSave: () => void;
  onSaveAs: () => void;
  onExportPDF: () => void;
  onJamSession: () => void;
  onGigMode: () => void;
  onHelp: () => void;
  onAccount: () => void;
  onSettings: () => void;
  isAccountOpen?: boolean;
  isSettingsOpen?: boolean;
  saveStatus?: 'idle' | 'saving' | 'saved';
  hasUnsavedChanges?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  onHome,
  onOpenChart,
  onSetlists,
  onNewChart,
  onOpenNewChartChoice,
  onPianoRec,
  onSave,
  onSaveAs,
  onExportPDF,
  onJamSession,
  onGigMode,
  onHelp,
  onAccount,
  onSettings,
  isAccountOpen = false,
  isSettingsOpen = false,
  saveStatus = 'idle',
  hasUnsavedChanges = false,
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isJamHovered, setIsJamHovered] = useState(false);
  const [isGigHovered, setIsGigHovered] = useState(false);

  return (
    <>
      {/* Desktop Header */}
      <header className="hidden w-full border-b border-neutral-800 h-16 lg:px-3 xl:px-4 items-center justify-between lg:gap-1.5 xl:gap-3" style={{ backgroundColor: '#161616' }}>
      
      {/* Left: Brand */}
      <div className="flex items-center lg:gap-2 xl:gap-3 lg:pr-2 xl:pr-3 shrink-0">
        <button onClick={onHome} className="flex items-center gap-3 select-none cursor-pointer hover:opacity-75 transition-opacity bg-transparent border-none p-0">
          {/* Logo Icon - Linked Squares */}
          <svg viewBox="-3 -3 63 63" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-[42px] w-auto shrink-0">
            <path d="M33 20.3102V11.3102C33 6.8919 29.4183 3.31018 25 3.31018H11C6.58172 3.31018 3 6.8919 3 11.3102V25.3102C3 29.7285 6.58172 33.3102 11 33.3102H20" stroke={t.gold} strokeWidth="6" strokeLinecap="round"/>
            <path d="M36 23.3102H45C49.4183 23.3102 53 26.8919 53 31.3102V45.3102C53 49.7285 49.4183 53.3102 45 53.3102H31C26.5817 53.3102 23 49.7285 23 45.3102V36.3102" stroke={t.gold} strokeOpacity="0.64" strokeWidth="6" strokeLinecap="round"/>
            <path d="M13 43.3102L43 13.3102" stroke={t.gold} strokeWidth="4" strokeLinecap="round"/>
          </svg>
          {/* Logo Text */}
          <div className="flex flex-col justify-center relative top-[-1px]">
            <span className="text-[#D3CCBD] leading-[0.8] relative" style={{ fontFamily: 'Konkhmer Sleokchher, serif', fontSize: '28px', left: '-4px', top: '1px' }}>CHORD</span>
            <span className="text-[#D3CCBD] leading-none tracking-widest uppercase relative" style={{ fontFamily: 'Kumbh Sans, sans-serif', fontSize: '14px', fontWeight: 200, letterSpacing: '0.05em', left: '-2px', top: '1px' }}>NOTATOR</span>
          </div>
        </button>
      </div>
      
      <div className="w-px h-8 bg-neutral-800 lg:mx-1.5 xl:mx-3 shrink-0" />

      {/* Center: Controls */}
      <div className="flex items-center lg:gap-1 xl:gap-1.5 overflow-x-auto no-scrollbar">
        <div className="flex items-center lg:gap-1 xl:gap-1.5 shrink-0">
          {/* Charts Button */}
          <button 
            onClick={onOpenChart}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm font-medium text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="20" x2="18" y2="10"></line>
              <line x1="12" y1="20" x2="12" y2="4"></line>
              <line x1="6" y1="20" x2="6" y2="14"></line>
            </svg>
            <span>Charts</span>
          </button>
          
          {/* Setlists Button */}
          <button 
            onClick={onSetlists}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm font-medium text-neutral-300 hover:text-white hover:bg-neutral-800 transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="8" y1="6" x2="21" y2="6"></line>
              <line x1="8" y1="12" x2="21" y2="12"></line>
              <line x1="8" y1="18" x2="21" y2="18"></line>
              <line x1="3" y1="6" x2="3.01" y2="6"></line>
              <line x1="3" y1="12" x2="3.01" y2="12"></line>
              <line x1="3" y1="18" x2="3.01" y2="18"></line>
            </svg>
            <span>Setlists</span>
          </button>
        </div>
        
        <div className="w-px h-6 bg-neutral-800 mx-1 shrink-0" />
        
        <div className="flex items-center gap-1 shrink-0">
          {/* New Chart Button */}
          <button 
            onClick={onNewChart}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm font-medium bg-neutral-800 hover:bg-neutral-700 text-white border border-neutral-700 transition-colors"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19"></line>
              <line x1="5" y1="12" x2="19" y2="12"></line>
            </svg>
            <span>New Chart</span>
          </button>
          
          {/* Save Button with Status Indicator */}
          <div className="hidden lg:flex items-center gap-1.5">
            <button 
              onClick={onSave}
              className={`p-2 rounded-md transition-colors ${
                saveStatus === 'saving' 
                  ? 'text-yellow-400 cursor-wait' 
                  : saveStatus === 'saved' 
                    ? 'text-green-400' 
                    : hasUnsavedChanges 
                      ? 'text-orange-400 hover:text-orange-300 hover:bg-neutral-800' 
                      : 'text-neutral-400 hover:text-white hover:bg-neutral-800'
              }`}
              title={
                saveStatus === 'saving' 
                  ? 'Saving...' 
                  : saveStatus === 'saved' 
                    ? 'Saved!' 
                    : hasUnsavedChanges 
                      ? 'Unsaved changes - Click to save' 
                      : 'Save'
              }
              disabled={saveStatus === 'saving'}
            >
              {saveStatus === 'saving' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="animate-spin">
                  <circle cx="12" cy="12" r="10" strokeOpacity="0.25"/>
                  <path d="M12 2a10 10 0 0 1 10 10" strokeLinecap="round"/>
                </svg>
              ) : saveStatus === 'saved' ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6L9 17l-5-5"/>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
              )}
            </button>
            {/* Save Status Text */}
            <span className={`text-xs transition-opacity duration-300 ${
              saveStatus === 'saved' ? 'opacity-100 text-green-400' : 
              saveStatus === 'saving' ? 'opacity-100 text-yellow-400' : 
              hasUnsavedChanges ? 'opacity-100 text-orange-400' : 'opacity-0'
            }`}>
              {saveStatus === 'saved' ? '✓ Saved' : 
               saveStatus === 'saving' ? 'Saving...' : 
               hasUnsavedChanges ? '●' : ''}
            </span>
          </div>
          
          {/* Save As Button */}
          <button 
            onClick={onSaveAs}
            className="px-2 py-1.5 rounded-md text-sm font-medium text-neutral-400 hover:text-white hover:bg-neutral-800 transition-colors hidden lg:block"
            title="Save As"
          >
            Save As
          </button>
          
          {/* Export PDF Button */}
          <button 
            onClick={onExportPDF}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm font-medium text-neutral-400 hover:text-white hover:bg-neutral-800 transition-colors"
            title="Export to PDF (Premium)"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            <span className="hidden xl:inline">PDF</span>
            <span className="text-xs text-cyan-400">✨</span>
          </button>
        </div>
        
        <div className="w-px h-6 bg-neutral-800 lg:mx-1 xl:mx-2 shrink-0" />
        
        <div className="flex items-center lg:gap-1.5 xl:gap-2.5 shrink-0">
          {/* Jam Session Button */}
          <button 
            onClick={onJamSession}
            onMouseEnter={() => setIsJamHovered(true)}
            onMouseLeave={() => setIsJamHovered(false)}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm font-medium border transition-colors"
            style={{ 
              color: '#2dd4bf',
              borderColor: 'rgba(45, 212, 191, 0.3)',
              backgroundColor: isJamHovered ? 'rgba(45, 212, 191, 0.1)' : 'transparent'
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
            <span>Jam Session</span>
          </button>
          
          {/* Gig Mode Button */}
          <button 
            onClick={onGigMode}
            onMouseEnter={() => setIsGigHovered(true)}
            onMouseLeave={() => setIsGigHovered(false)}
            className="flex items-center gap-1.5 px-2 py-1.5 rounded-md text-sm font-medium text-white transition-colors"
            style={{
              backgroundColor: isGigHovered ? '#8b5cf6' : '#7c3aed',
              boxShadow: '0 0 15px rgba(139, 92, 246, 0.3)'
            }}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"></polygon>
            </svg>
            <span>Gig Mode</span>
          </button>
        </div>
      </div>

      {/* Right: Meta */}
      <div className="flex items-center lg:gap-1.5 xl:gap-2 lg:pl-2 xl:pl-3 border-l border-neutral-800 h-8 shrink-0">
        {/* Help Button */}
        <button 
          onClick={onHelp}
          className="text-neutral-400 hover:text-white p-2 rounded-full hover:bg-neutral-800 transition-colors"
          title="Help"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"></circle>
            <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
            <line x1="12" y1="17" x2="12.01" y2="17"></line>
          </svg>
        </button>

        {/* Settings Button */}
        <button
          onClick={onSettings}
          className={`p-2 rounded-full transition-colors ${
            isSettingsOpen
              ? 'text-blue-400 bg-blue-500/20'
              : 'text-neutral-400 hover:text-white hover:bg-neutral-800'
          }`}
          title="Settings & Tools"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
        </button>
        
        {/* User/Account Button */}
        <button
          onClick={onAccount}
          className={`p-2 rounded-full transition-colors ${
            isAccountOpen
              ? 'text-amber-400 bg-amber-500/20'
              : 'text-neutral-400 hover:text-white hover:bg-neutral-800'
          }`}
          title="Account & Subscription"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
            <circle cx="12" cy="7" r="4"></circle>
          </svg>
        </button>
      </div>
      </header>

      {/* Mobile Header */}
      <header className="lg:hidden w-full border-b h-16 px-4 flex items-center justify-between" style={{ backgroundColor: t.bg, borderBottomColor: 'rgba(86,70,60,0.5)' }}>
        {/* EDIT MODE badge */}
        <div className="flex items-center gap-2">
          <img src="/figma-assets/icons/edit-mode-icon.svg" width="21" height="21" alt="" />
          <span style={{ fontFamily: 'Kumbh Sans, sans-serif', fontWeight: 500, fontSize: '16.8px', letterSpacing: '0.03em', textTransform: 'uppercase', color: t.lime }}>
            Edit Mode
          </span>
        </div>

        {/* Hamburger Button */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-md transition-colors"
          style={{ color: t.beige }}
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="12" x2="21" y2="12"></line>
              <line x1="3" y1="6" x2="21" y2="6"></line>
              <line x1="3" y1="18" x2="21" y2="18"></line>
            </svg>
          )}
        </button>
      </header>

      {/* Mobile Menu Drawer */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50" style={{ backgroundColor: 'rgba(26,20,16,0.6)' }} onClick={() => setIsMobileMenuOpen(false)}>
          <div className="absolute right-0 top-0 h-full w-80 max-w-[90vw] shadow-2xl flex flex-col" style={{ backgroundColor: '#404A48', borderLeft: '1px solid rgba(255,255,255,0.08)' }} onClick={(e) => e.stopPropagation()}>
            {/* Drawer header */}
            <div className="flex items-center justify-between px-5 py-4" style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <span style={{ fontFamily: 'Kumbh Sans, sans-serif', textTransform: 'uppercase', fontSize: '14px', fontWeight: 500, color: '#D3CCBD', letterSpacing: '0.08em' }}>Menu</span>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-1.5 rounded transition-colors"
                style={{ color: '#D3CCBD' }}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            {/* Menu items */}
            <div className="flex-1 overflow-y-auto px-3 py-3 space-y-1">

              {/* Gig Mode */}
              <button
                onClick={() => { onGigMode(); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors"
                style={{ color: '#53D2D4', border: '1px solid rgba(83,210,212,0.25)', backgroundColor: 'rgba(83,210,212,0.07)', fontFamily: 'Kumbh Sans, sans-serif', textTransform: 'uppercase', fontSize: '14px', fontWeight: 500, letterSpacing: '0.08em' }}
              >
                <img src="/figma-assets/icons/side-menu/gig-mode.svg" width="24" height="24" alt="" />
                <span>Gig Mode</span>
              </button>

              {/* Jam Session */}
              <button
                onClick={() => { onJamSession(); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors"
                style={{ color: '#E4393F', border: '1px solid rgba(228,57,63,0.25)', backgroundColor: 'rgba(228,57,63,0.07)', fontFamily: 'Kumbh Sans, sans-serif', textTransform: 'uppercase', fontSize: '14px', fontWeight: 500, letterSpacing: '0.08em' }}
              >
                <img src="/figma-assets/icons/side-menu/jam-session.svg" width="24" height="24" alt="" />
                <span>Jam Session</span>
              </button>

              <div style={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.1)', margin: '6px 0' }} />

              {/* New Chart */}
              <button
                onClick={() => { (onOpenNewChartChoice ?? onNewChart)(); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-[#323D3B]"
                style={{ color: '#D3CCBD', fontFamily: 'Kumbh Sans, sans-serif', textTransform: 'uppercase', fontSize: '14px', fontWeight: 500, letterSpacing: '0.08em' }}
              >
                <img src="/figma-assets/icons/side-menu/new-chart.svg" width="24" height="24" alt="" />
                <span>New</span>
              </button>

              {/* Piano Rec */}
              {onPianoRec && (
                <button
                  onClick={() => { onPianoRec(); setIsMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-[#323D3B]"
                  style={{ color: '#D3CCBD', fontFamily: 'Kumbh Sans, sans-serif', textTransform: 'uppercase', fontSize: '14px', fontWeight: 500, letterSpacing: '0.08em' }}
                >
                  <svg width="24" height="24" viewBox="0 0 44 44" fill="none">
                    <path d="M24.5996 0C30.4538 0 35.1992 4.74539 35.1992 10.5996V16.0977L40.6484 10.6484C41.3807 9.9162 42.5685 9.9162 43.3008 10.6484C44.033 11.3807 44.033 12.5685 43.3008 13.3008L13.3008 43.3008C12.5685 44.033 11.3807 44.033 10.6484 43.3008C9.9162 42.5685 9.9162 41.3807 10.6484 40.6484L16.0977 35.1992H10.5996C4.74539 35.1992 0 30.4538 0 24.5996V10.5996C0 4.74539 4.74539 0 10.5996 0H24.5996ZM10.5996 5.19922C7.61727 5.19922 5.19922 7.61727 5.19922 10.5996V24.5996C5.19922 27.5819 7.61727 30 10.5996 30H19.5996C20.0856 30 20.5387 30.1361 20.9277 30.3682L30.3682 20.9277C30.1361 20.5387 30 20.0856 30 19.5996V10.5996C30 7.61727 27.5819 5.19922 24.5996 5.19922H10.5996ZM23.5996 25H7.59961V23H23.5996V25ZM26.9033 7.28516C27.1663 7.20086 27.4479 7.34548 27.5322 7.6084C27.5597 7.69401 27.5603 7.78118 27.543 7.86328V16.7832C27.5429 16.841 27.5308 16.8956 27.5127 16.9473C27.5312 17.0565 27.543 17.1687 27.543 17.2832C27.5428 18.3876 26.6474 19.2832 25.543 19.2832C24.4386 19.2831 23.5432 18.3875 23.543 17.2832C23.543 16.1787 24.4385 15.2833 25.543 15.2832C25.9074 15.2832 26.2487 15.3814 26.543 15.5518V8.4502L21.543 10.0537V18.7832C21.5429 18.841 21.5308 18.8956 21.5127 18.9473C21.5312 19.0565 21.543 19.1687 21.543 19.2832C21.5428 20.3876 20.6474 21.2832 19.543 21.2832C18.4386 21.2831 17.5432 20.3875 17.543 19.2832C17.543 18.1787 18.4385 17.2833 19.543 17.2832C19.9074 17.2832 20.2487 17.3814 20.543 17.5518V9.7832C20.543 9.67472 20.578 9.57413 20.6367 9.49219C20.6891 9.35534 20.7998 9.24255 20.9502 9.19434L26.9033 7.28516ZM15.5996 18H7.59961V16H15.5996V18ZM11.6797 14H7.59961V12H11.6797V14ZM15.5996 10H7.59961V8H15.5996V10Z" fill="#DB54F7"/>
                  </svg>
                  <span>Piano Rec</span>
                </button>
              )}

              {/* Open */}
              <button
                onClick={() => { onOpenChart(); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-[#323D3B]"
                style={{ color: '#D3CCBD', fontFamily: 'Kumbh Sans, sans-serif', textTransform: 'uppercase', fontSize: '14px', fontWeight: 500, letterSpacing: '0.08em' }}
              >
                <img src="/figma-assets/icons/side-menu/open.svg" width="24" height="24" alt="" />
                <span>Open</span>
              </button>

              {/* Setlists */}
              <button
                onClick={() => { onSetlists(); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-[#323D3B]"
                style={{ color: '#D3CCBD', fontFamily: 'Kumbh Sans, sans-serif', textTransform: 'uppercase', fontSize: '14px', fontWeight: 500, letterSpacing: '0.08em' }}
              >
                <img src="/figma-assets/icons/side-menu/setlists.svg" width="24" height="24" alt="" />
                <span>Setlists</span>
              </button>

              <div style={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.1)', margin: '6px 0' }} />

              {/* Save */}
              <button
                onClick={() => { onSave(); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-[#323D3B]"
                style={{ color: '#D3CCBD', fontFamily: 'Kumbh Sans, sans-serif', textTransform: 'uppercase', fontSize: '14px', fontWeight: 500, letterSpacing: '0.08em' }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
                <span>Save</span>
              </button>

              {/* Save As */}
              <button
                onClick={() => { onSaveAs(); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-[#323D3B]"
                style={{ color: '#D3CCBD', fontFamily: 'Kumbh Sans, sans-serif', textTransform: 'uppercase', fontSize: '14px', fontWeight: 500, letterSpacing: '0.08em' }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z"></path>
                  <polyline points="17 21 17 13 7 13 7 21"></polyline>
                  <polyline points="7 3 7 8 15 8"></polyline>
                </svg>
                <span>Save As</span>
              </button>

              {/* Export PDF */}
              <button
                onClick={() => { onExportPDF(); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-[#323D3B]"
                style={{ color: '#D3CCBD', fontFamily: 'Kumbh Sans, sans-serif', textTransform: 'uppercase', fontSize: '14px', fontWeight: 500, letterSpacing: '0.08em' }}
              >
                <div className="flex items-center gap-3">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                    <polyline points="14 2 14 8 20 8"></polyline>
                    <line x1="16" y1="13" x2="8" y2="13"></line>
                    <line x1="16" y1="17" x2="8" y2="17"></line>
                    <polyline points="10 9 9 9 8 9"></polyline>
                  </svg>
                  <span>Export PDF</span>
                </div>
                <span style={{ fontSize: '11px', color: '#D4A853' }}>Premium</span>
              </button>

              <div style={{ height: '1px', backgroundColor: 'rgba(255,255,255,0.1)', margin: '6px 0' }} />

              {/* Help — keep actual icon */}
              <button
                onClick={() => { onHelp(); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-[#323D3B]"
                style={{ color: '#D3CCBD', fontFamily: 'Kumbh Sans, sans-serif', textTransform: 'uppercase', fontSize: '14px', fontWeight: 500, letterSpacing: '0.08em' }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <span>Help</span>
              </button>

              {/* Settings & Tools — keep actual icon */}
              <button
                onClick={() => { onSettings(); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-[#323D3B]"
                style={{ color: '#D3CCBD', fontFamily: 'Kumbh Sans, sans-serif', textTransform: 'uppercase', fontSize: '14px', fontWeight: 500, letterSpacing: '0.08em' }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3"></circle>
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
                </svg>
                <span>Settings & Tools</span>
              </button>

              {/* Account & Subscription — keep actual icon */}
              <button
                onClick={() => { onAccount(); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors hover:bg-[#323D3B]"
                style={{ color: '#D3CCBD', fontFamily: 'Kumbh Sans, sans-serif', textTransform: 'uppercase', fontSize: '14px', fontWeight: 500, letterSpacing: '0.08em' }}
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span>Account & Subscription</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;